import type { ReactNode } from 'react';
import './screen-header.css';

/**
 * WHAT THIS SCREEN IS FOR, IN A SENTENCE, BEFORE ANYTHING ELSE.
 *
 * ── THE PROBLEM THIS EXISTS TO FIX ────────────────────────────────────────
 *
 * Twenty of the console's twenty-one screens opened with a row of numbers. An
 * operator arriving at one — new to the job, or sent there by somebody else, or
 * returning to a module they use twice a month — was shown four figures and a
 * table and left to work out what the screen was for from the figures.
 *
 * The explanations existed. Most of these files carry a module header comment
 * saying exactly why the screen exists and who reaches for it, written
 * carefully, and read by nobody who was not already editing the file. The prose
 * was always for the operator; it was just in the wrong place.
 *
 * ── `purpose` IS REQUIRED, AND THAT IS THE WHOLE MECHANISM ────────────────
 *
 * Not optional with a sensible default. A screen cannot render this header
 * without saying what it is for, so the next module added to the console cannot
 * repeat the omission by forgetting — it is a type error, caught before the
 * screen is ever opened. `screen-purpose.test.ts` then holds the other half:
 * every routed screen must actually use this.
 *
 * ── AND THE COUNT LINE SURVIVES, SEPARATELY ───────────────────────────────
 *
 * `meta` is a slot rather than a string because several screens derive a scope
 * caption from the live filter — "3 brands · 412 players" — and that is a
 * different kind of sentence from the purpose. Folding them together would have
 * meant either losing the derived part or writing the scope as a constant, and
 * `dashboard-scope-captions.test.ts` exists because a hard-coded claim about
 * breadth was once wrong on screen while the figure beside it was right.
 *
 * So: `purpose` is fixed prose about the screen; `meta` is whatever the screen
 * currently knows. One does not become the other.
 */
export interface ScreenHeaderProps {
  /** The module's name, as the sidebar names it. */
  title: string;
  /**
   * ONE sentence: what this screen is for, and who reaches for it.
   *
   * Required. A screen with nothing to say here is a screen whose reason for
   * existing nobody has written down, and that is worth finding out at the
   * point somebody tries to add it rather than after it ships.
   *
   * Written for an operator. No table, column, route, permission key or
   * constraint name — `screens-must-not-name-schema` and the no-jargon tests
   * both police this, and they police it on this prop like any other text.
   */
  purpose: string;
  /**
   * The derived line the screen already showed: counts, the brand in scope, the
   * period. A NODE rather than a string, because these are computed from live
   * state and several carry their own markup.
   */
  meta?: ReactNode;
  /** Controls that belong to the whole screen — an export, a "New…" button. */
  actions?: ReactNode;
}

export function ScreenHeader({ title, purpose, meta, actions }: ScreenHeaderProps) {
  return (
    <header className="screen-header">
      <div className="screen-header-text">
        {/*
          `h3`, WHICH IS THE LEVEL EVERY CONSOLE SCREEN ALREADY USED.

          This rendered an `h2` first. Nothing on screen looked different —
          `.screen-header-title` sets the size — and twenty-one screens quietly
          changed heading level in a commit about adding a sentence. The e2e
          suite locates several screens by tag (`locator('h3').filter({ hasText:
          'Platform management' })`), so those went from finding a heading to
          finding nothing, and every click that followed timed out against a
          page the test had not actually reached.

          Whether a screen title ought to be an h2 is a real question — this
          shell renders no h1 or h2 at all, so the hierarchy starts at 3 — but it
          is a question about the whole console's document outline, and it does
          not belong smuggled into a change about guidance. If it moves, it moves
          deliberately, with the suite moved with it.
        */}
        <h3 className="screen-header-title">{title}</h3>
        {/* The purpose sits directly under the name and above everything the
            screen computes, because somebody who does not yet know what the
            screen is for cannot use the numbers. */}
        <p className="screen-header-purpose">{purpose}</p>
        {meta !== undefined && <div className="screen-header-meta text-subtle">{meta}</div>}
      </div>
      {actions !== undefined && <div className="screen-header-actions">{actions}</div>}
    </header>
  );
}
